import { Value } from 'baseui/select'
import React, { useContext, useEffect, useState } from 'react'
import { v1 as uuidv1 } from 'uuid'
import { DateTime } from 'luxon'
import { useSetDeviceForceUpdate } from '../../DeviceConfig/utils'
import { StationContext } from '../StationContext'
import { useGetCoordinatesParkingDetections, useGetSystemImageQuery } from '../utils/station-hooks'
import { TrajectoryConfiguration } from './TrajectoryConfiguration'
import { TrajectoryConfigurationProvider } from './TrajectoryConfigurationContext'
import { TrajectoryViewCoordinatesState, TrajectoryViewState } from './trajectory_configuration_types'
import { input_transformation_dropdown } from './util_inline_tool/input_transformation_enable_event_classification'
import { input_tranformation_polygon } from './util_inline_tool/input_transformation_polygon'
import { useLiveViewHandler } from './hooks/useLiveViewHandler'

interface IImageSize {
    initialized: boolean
    width: number
    height: number
    image_mutate_factor: number
    text: string
}
export const TrajectoryConfigurationContainer = () => {
    const stationContext = useContext(StationContext)

    //VARIABLES
    const INIT_IMAGE_SIZE: IImageSize = {
        initialized: false,
        width: 0,
        height: 0,
        image_mutate_factor: 0,
        text: 'loading...'
    }

    //STATES
    const [latestImage, setLatestImage] = useState<boolean>(true)
    const [imagePrerender, setImagePrerender] = useState<number>(0)
    const [image, setImage] = useState<HTMLImageElement>()
    const [imageSize, setImageSize] = useState<IImageSize>(INIT_IMAGE_SIZE)
    const [dataDB, setDataDB] = useState<{
        enableEventClassificationData?: any
        polygonData?: any
    }>({})
    const [data, setData] = useState<{
        enableEventClassificationData?: any
        polygonData?: any
    }>({})
    const [filterDataDB, setFilterDataDB] = useState<any>([])
    const [filterData, setFilterData] = useState<any>([])
    const [currentFilter, setCurrentFilter] = useState<number | undefined>()
    const [validationModalStatePolygon, setValidationModalStatePolygon] = useState<any>({
        show: false,
        description: ''
    })
    const [viewMode, setViewModeState] = React.useState<Value>([])
    const [initialTime, setInitialTime] = useState<string>()
    const [trajectoryViewCoordinates, setTrajectoryViewCoordinates] = useState<TrajectoryViewCoordinatesState>({
        vehicle_trajectories: [],
        lp_trajectories: []
    })
    const [trajectoryViewDirection, setTrajectoryViewDirection] = useState<string>('IN')
    const [trajectoryViewMissingLP, setCoordinasetTrajectoryViewMissingLP] = useState<boolean>(false)
    const [enableTrajectoryView, setEnableTrajectoryView] = useState<boolean>(true)
    const [vehicleDetectionsTrajectoryView, setVehicleDetectionsTrajectoryView] = useState<TrajectoryViewState>({
        view_trajectories: [0, 0],
        available_trajectories: 0
    })
    const [lpDetectionsTrajectoryView, setLpDetectionsTrajectoryView] = useState<TrajectoryViewState>({
        view_trajectories: [0, 0],
        available_trajectories: 0
    })
    const [bestDetection, setBestDetection] = useState<number>(0)
    const {
        loading,
        liveViewScreenshot,
        selectedImageSource,
        setSelectedImageSource,
        loadLiveViewScreenshotHandler,
        removeLiveViewUrl
    } = useLiveViewHandler(stationContext)

    //SET STATES
    //S1: latest image
    const setLatestImageState = (latestImage: any | null | undefined): void => {
        setLatestImage(latestImage)
    }
    //S2: data polygon
    const setDataState = (key: string, newData: any) => {
        setData((prevData: any) => ({
            ...prevData,
            [key]: {
                ...prevData[key],
                ...newData
            }
        }))
    }
    //S3: validation show polygon
    const setValidationModalStatePolygonState = (
        show: any | null | undefined,
        description: any | null | undefined
    ): void => {
        setValidationModalStatePolygon({ show: show, description: description })
    }
    //S4: current filter
    const setCurrentFilterState = (index: number | undefined): void => {
        setCurrentFilter(index)
    }
    //S5: set filter data
    const setFilterDataState = (key: string, newData: any, index: number) => {
        setFilterData((prevData: any) => {
            const updatedFilterData = [...prevData]
            updatedFilterData[index] = {
                ...updatedFilterData[index],
                [key]: newData,
                updation_time: new Date().toISOString()
            }
            return updatedFilterData
        })
    }
    //S6: add new filter
    const addNewFilter = () => {
        setFilterData((prevData: any) => {
            const newFilter = {
                creation_time: new Date().toISOString(),
                description: '',
                id: uuidv1(),
                name: '',
                priority: 1,
                properties: {
                    _rectangle_in: { _x0: 0, _x1: 300, _y0: 0, _y1: 300 },
                    _rectangle_out: { _x0: 500, _x1: 900, _y0: 400, _y1: 800 }
                },
                type: 'FilterInvalidTrajectory_Soft',
                updation_time: new Date().toISOString()
            }
            const updatedFilterData = [...prevData, newFilter]
            setCurrentFilter(updatedFilterData.length - 1)
            return updatedFilterData
        })
    }
    //S7: delete filter
    const deleteFilter = (index: number) => {
        setFilterData((prevData: any) => {
            const updatedFilterData = prevData.filter((_: any, i: number) => i !== Number(index))
            setCurrentFilter(undefined)
            return updatedFilterData
        })
    }
    //S11
    const setTrajectoryViewDirectionState = (value: string): void => {
        setTrajectoryViewDirection(value)
    }
    //S12
    const setCoordinasetTrajectoryViewMissingLPState = (value: boolean): void => {
        setCoordinasetTrajectoryViewMissingLP(value)
    }
    //S13
    const setEnableTrajectoryViewState = (value: boolean): void => {
        setEnableTrajectoryView(value)
    }
    //S14
    const setVehicleDetectionsTrajectoryViewState = (key: keyof TrajectoryViewState, value: any): void => {
        if (key === 'view_trajectories') {
            setVehicleDetectionsTrajectoryView((prevState) => ({
                ...prevState,
                [key]: value
            }))
        } else if (key === 'available_trajectories') {
            setVehicleDetectionsTrajectoryView((prevState) => ({
                ...prevState,
                [key]: value
            }))
        }
    }

    //S15
    const setLpDetectionsTrajectoryViewState = (key: keyof TrajectoryViewState, value: any): void => {
        if (key === 'view_trajectories') {
            setLpDetectionsTrajectoryView((prevState) => ({
                ...prevState,
                [key]: value
            }))
        } else if (key === 'available_trajectories') {
            setLpDetectionsTrajectoryView((prevState) => ({
                ...prevState,
                [key]: value
            }))
        }
    }

    //QUERIES
    //Q1: get system image + handling loading state
    const { data: systemImage, loadingFetchSystemImage } = useGetSystemImageQuery(
        stationContext.deviceData?.uuid || null,
        latestImage || null
    )
    const { setDeviceForceUpdate, loadingForceUpdate } = useSetDeviceForceUpdate()

    //Q2: trajectory view
    const { data: coordinatesData } = useGetCoordinatesParkingDetections(
        initialTime,
        stationContext.deviceData?.uuid,
        trajectoryViewDirection,
        trajectoryViewMissingLP,
        stationContext.getLotId()
    )
    useEffect(() => {
        const vehicle_trajectories: any = []
        const lp_trajectories: any = []
        if (coordinatesData && Array.isArray(coordinatesData.parking_detections)) {
            coordinatesData.parking_detections.forEach((data: any) => {
                const pre_json = data.coordinates
                const json_data = typeof pre_json === 'string' ? JSON.parse(pre_json) : pre_json
                if (
                    json_data.vehicle_trajectory &&
                    json_data.vehicle_trajectory.hasOwnProperty('x') &&
                    json_data.vehicle_trajectory.x.length !== 0
                ) {
                    vehicle_trajectories.push({
                        trajectory: json_data.vehicle_trajectory,
                        best_detection: json_data.best_detection.vehicle
                    })
                }
                if (
                    json_data.lp_trajectory &&
                    json_data.lp_trajectory.hasOwnProperty('x') &&
                    json_data.lp_trajectory.x.length !== 0
                ) {
                    lp_trajectories.push({
                        trajectory: json_data.lp_trajectory,
                        best_detection: json_data.best_detection.lp
                    })
                }
            })
        }

        setTrajectoryViewCoordinates({
            vehicle_trajectories: vehicle_trajectories,
            lp_trajectories: lp_trajectories
        })
        setVehicleDetectionsTrajectoryViewState('available_trajectories', vehicle_trajectories.length)
        setLpDetectionsTrajectoryViewState('available_trajectories', lp_trajectories.length)
        setVehicleDetectionsTrajectoryViewState('view_trajectories', [0, 0])
        setLpDetectionsTrajectoryViewState('view_trajectories', [0, 0])
    }, [coordinatesData])

    //HOOKS
    //H1: checks stationContext.deviceData for changes and resets polygon/eventClassificationData accordingly
    useEffect(() => {
        if (stationContext.deviceData && imageSize.width !== 0) {
            const enableEventClassificationDataTemp = input_transformation_dropdown(stationContext.deviceData)
            const polygonDataTemp = input_tranformation_polygon(
                stationContext.deviceData,
                imageSize.width,
                imageSize.height
            )
            if (
                JSON.stringify(enableEventClassificationDataTemp) !==
                    JSON.stringify(dataDB.enableEventClassificationData) ||
                JSON.stringify(polygonDataTemp) !== JSON.stringify(dataDB.polygonData)
            ) {
                const newData = {
                    enableEventClassificationData: enableEventClassificationDataTemp,
                    polygonData: polygonDataTemp
                }
                setDataDB(newData)
                setData(newData)
                // console.log(
                //     'DATA DB CHANGED: overwriting dataDB and data:',
                //     enableEventClassificationDataTemp,
                //     polygonDataTemp
                // )
            }
        }
    }, [stationContext.deviceData, dataDB, imageSize])
    // console.log(liveViewScreenshot)

    const getRelativeTimeString = (date: Date) => {
        return DateTime.fromISO(date.toISOString()).toRelative({
            locale: 'en'
        })
    }
    //H2: system image handling + ImageSize State
    useEffect(() => {
        // console.log('latest image', latestImage, loading, liveViewScreenshot)
        setImage(undefined)
        setImageSize({
            initialized: true,
            width: 750,
            height: 421.875,
            image_mutate_factor: 1,
            text: 'loading...'
        })
        if (liveViewScreenshot && selectedImageSource === 1) {
            const image = new window.Image()
            // Only hand a fully-decoded image to Konva. Rendering it while still loading — or leaving a
            // broken image in state after a failed load — makes Konva's drawImage throw InvalidStateError.
            image.onerror = () => setImage(undefined)
            image.onload = () => {
                setImage(image)
                const FIXED_WIDTH = 750
                const image_mutate_factor = FIXED_WIDTH / image.width
                const currentDate = new Date()
                const relativeTime = getRelativeTimeString(currentDate)
                setImageSize({
                    initialized: true,
                    width: image.width * image_mutate_factor,
                    height: image.height * image_mutate_factor,
                    image_mutate_factor: image_mutate_factor,
                    text:
                        'image fetched successfully: latest image = ' +
                        latestImage +
                        ', timestamp = ' +
                        currentDate.toLocaleString() +
                        ' (' +
                        relativeTime +
                        ')'
                })
            }
            image.src = liveViewScreenshot
            return
        }
        if (systemImage && systemImage.signed_url) {
            if (imagePrerender === 0 && loadingFetchSystemImage === false) {
                //Rendering LI=true RI=true
                setImagePrerender(1)

                setLatestImage(false)
            }
            if (imagePrerender === 1 && loadingFetchSystemImage === false) {
                //Rendering LI=false RI=true
                setImagePrerender(2)

                setLatestImage(true)
            }
            if (imagePrerender === 2 && loadingFetchSystemImage === false) {
                //Rendering LI=true RI=false
                setImagePrerender(3)

                setLatestImage(false)
            }
            //Rendering LI=false RI=false

            const timestampString = systemImage.timestamp ?? ''
            const timestampDate = new Date(timestampString)
            const formattedTimestamp = timestampDate.toLocaleString()

            const image = new window.Image()
            // Only hand a fully-decoded image to Konva. Rendering it while still loading — or leaving a
            // broken image in state after a failed load — makes Konva's drawImage throw InvalidStateError.
            image.onerror = () => setImage(undefined)
            image.onload = () => {
                setImage(image)
                const FIXED_WIDTH = 750
                const image_mutate_factor = FIXED_WIDTH / image.width
                const relativeTime = getRelativeTimeString(timestampDate)
                setImageSize({
                    initialized: true,
                    width: image.width * image_mutate_factor,
                    height: image.height * image_mutate_factor,
                    image_mutate_factor: image_mutate_factor,
                    text:
                        'image fetched successfully: latest image = ' +
                        latestImage +
                        ', timestamp = ' +
                        formattedTimestamp +
                        ' (' +
                        relativeTime +
                        ')'
                })
            }
            image.src = systemImage.signed_url ?? ''
        } else {
            if (loadingFetchSystemImage) {
                return
            }
            setImage(undefined)
            setImageSize({
                initialized: true,
                width: 750,
                height: 421.875,
                image_mutate_factor: 1,
                text: 'error fetching image'
            })
        }
    }, [
        systemImage,
        loadingFetchSystemImage,
        latestImage,
        imagePrerender,
        liveViewScreenshot,
        loading,
        selectedImageSource
    ])

    //H3: checks stationContext.selectedStationData for changes and resets filterDataDB accordingly
    useEffect(() => {
        if (stationContext.selectedStationData) {
            const filterDataTemp = stationContext.selectedStationData.config
                ? stationContext.selectedStationData.config.filters || []
                : []
            if (JSON.stringify(filterDataTemp) !== JSON.stringify(filterDataDB.filters)) {
                setFilterDataDB(filterDataTemp)
                setFilterData(filterDataTemp)
            }
        }
    }, [stationContext.selectedStationData, filterDataDB.filters])

    //H4: reset initial state when station name changes
    useEffect(() => {
        if (stationContext.selectedStationData?.station_name && imagePrerender !== 0) {
            setImagePrerender(0)
            setLatestImage(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stationContext.selectedStationData])

    useEffect(() => {
        const twoDaysAgo = new Date()
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)
        const year = twoDaysAgo.getFullYear()
        const month = (twoDaysAgo.getMonth() + 1).toString().padStart(2, '0')
        const day = twoDaysAgo.getDate().toString().padStart(2, '0')
        const hours = twoDaysAgo.getHours().toString().padStart(2, '0')
        const minutes = twoDaysAgo.getMinutes().toString().padStart(2, '0')
        const seconds = twoDaysAgo.getSeconds().toString().padStart(2, '0')
        const formattedTimestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.000000+00`
        setInitialTime(formattedTimestamp)
    }, [])

    return (
        <div>
            <TrajectoryConfigurationProvider
                context={{
                    loadingFetchSystemImage: loadingFetchSystemImage,
                    dataDB: dataDB,
                    data: data,
                    setData: setDataState,
                    filterData: filterData,
                    setFilterData: setFilterDataState,
                    filterDataDB: filterDataDB,
                    addNewFilter: addNewFilter,
                    deleteFilter: deleteFilter,
                    currentFilter: currentFilter,
                    setCurrentFilter: setCurrentFilterState,
                    image: image,
                    imageSize: imageSize,
                    latestImage: latestImage,
                    setLatestImage: setLatestImageState,
                    validationModalStatePolygon: validationModalStatePolygon,
                    setValidationModalStatePolygon: setValidationModalStatePolygonState,
                    viewMode,
                    setViewMode: setViewModeState,
                    trajectoryViewCoordinates: trajectoryViewCoordinates,
                    setTrajectoryViewDirection: setTrajectoryViewDirectionState,
                    setCoordinasetTrajectoryViewMissingLP: setCoordinasetTrajectoryViewMissingLPState,
                    setEnableTrajectoryView: setEnableTrajectoryViewState,
                    trajectoryViewDirection: trajectoryViewDirection,
                    trajectoryViewMissingLP: trajectoryViewMissingLP,
                    enableTrajectoryView: enableTrajectoryView,
                    vehicleDetectionsTrajectoryView: vehicleDetectionsTrajectoryView,
                    setVehicleDetectionsTrajectoryView: setVehicleDetectionsTrajectoryViewState,
                    lpDetectionsTrajectoryView: lpDetectionsTrajectoryView,
                    setLpDetectionsTrajectoryView: setLpDetectionsTrajectoryViewState,
                    bestDetection: bestDetection,
                    setBestDetection: setBestDetection,
                    setDeviceForceUpdate: setDeviceForceUpdate,
                    loadingForceUpdate,
                    loadLiveViewScreenshotHandler,
                    removeLiveViewUrl,
                    loading,
                    selectedImageSource,
                    setSelectedImageSource
                }}
            >
                <TrajectoryConfiguration />
            </TrajectoryConfigurationProvider>
        </div>
    )
}
